import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { Viewport, type ViewportHandle } from './components/Viewport';
import { ToolPalette } from './components/ToolPalette';
import { TopBar } from './components/TopBar';
import { Inspector } from './components/Inspector';
import { StatusBar } from './components/StatusBar';
import { HelpModal } from './components/HelpModal';
import type { ExportFormat } from './engine/exporters';
import type { SavedScene, ToolId } from './model/types';
import { useStudioStore } from './state/useStudioStore';
import { downloadModel, readAutosave, readModelFile, saveAutosave } from './utils/fileIO';
import { AdRail } from './components/AdRail';
import { AdPreferences } from './components/AdPreferences';
import { openAdPreferences } from './ads/sdk';
import './styles.css';

interface ToastState {
  id: number;
  kind: 'success' | 'error' | 'info';
  message: string;
}

const shortcuts: Record<string, ToolId> = {
  v: 'select',
  w: 'wall',
  q: 'room',
  f: 'slab',
  b: 'box',
  l: 'line',
  d: 'door',
  n: 'window',
  p: 'pushpull',
  m: 'move',
  r: 'rotate',
  s: 'scale',
  g: 'paint',
  t: 'measure',
  e: 'eraser',
};

export default function App(): JSX.Element {
  const viewportRef = useRef<ViewportHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const document = useStudioStore((state) => state.document);
  const selectedIds = useStudioStore((state) => state.selectedIds);
  const setActiveTool = useStudioStore((state) => state.setActiveTool);
  const replaceDocument = useStudioStore((state) => state.replaceDocument);
  const undo = useStudioStore((state) => state.undo);
  const redo = useStudioStore((state) => state.redo);
  const deleteEntities = useStudioStore((state) => state.deleteEntities);
  const duplicateSelected = useStudioStore((state) => state.duplicateSelected);
  const newBlank = useStudioStore((state) => state.newBlank);
  const newDemo = useStudioStore((state) => state.newDemo);
  const addScene = useStudioStore((state) => state.addScene);
  const setStatus = useStudioStore((state) => state.setStatus);

  const notify = (message: string, kind: ToastState['kind'] = 'success') => {
    const id = Date.now();
    setToast({ id, kind, message });
    window.setTimeout(() => setToast((current) => current?.id === id ? null : current), 3400);
  };

  useEffect(() => {
    let cancelled = false;
    void readAutosave().then((autosave) => {
      if (!cancelled && autosave) {
        replaceDocument(autosave);
        notify(`Recovered local autosave: ${autosave.name}`, 'info');
      }
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => { void saveAutosave(document); }, 550);
    return () => window.clearTimeout(handle);
  }, [document]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping = target?.matches('input, textarea, select, [contenteditable="true"]');
      if (event.defaultPrevented || isTyping || target?.closest('dialog[open]') || (target?.closest('[role="menu"], [data-header-menu]') && !event.ctrlKey && !event.metaKey)) return;

      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && key === 's') {
        event.preventDefault();
        downloadModel(useStudioStore.getState().document);
        notify('Editable .ccmodel downloaded');
        return;
      }
      if ((event.ctrlKey || event.metaKey) && key === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && key === 'y') {
        event.preventDefault();
        redo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && key === 'd') {
        event.preventDefault();
        duplicateSelected();
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedIds.length) {
          event.preventDefault();
          deleteEntities(selectedIds);
        }
        return;
      }
      if (key === '?' || (event.shiftKey && key === '/')) {
        setHelpOpen(true);
        return;
      }
      const tool = shortcuts[key];
      if (tool && !event.ctrlKey && !event.metaKey && !event.altKey) setActiveTool(tool);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteEntities, duplicateSelected, redo, selectedIds, setActiveTool, undo]);

  const openFile = async (file: File) => {
    try {
      const next = await readModelFile(file);
      replaceDocument(next);
      viewportRef.current?.fitView(false);
      notify(`Opened ${next.name}`);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Could not open that model.', 'error');
    }
  };

  const exportModel = async (format: ExportFormat) => {
    try {
      setStatus(`Preparing ${format.toUpperCase()} export…`);
      await viewportRef.current?.exportModel(format);
      setStatus(`Exported ${format.toUpperCase()}`);
      notify(`${format.toUpperCase()} export downloaded`);
    } catch (error) {
      notify(error instanceof Error ? error.message : `Could not export ${format.toUpperCase()}.`, 'error');
    }
  };

  const capture = async () => {
    try {
      await viewportRef.current?.capturePng();
      notify('Viewport PNG downloaded');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Could not capture the viewport.', 'error');
    }
  };

  const saveScene = () => {
    const index = useStudioStore.getState().document.scenes.length + 1;
    const snapshot = viewportRef.current?.getSceneSnapshot(`Scene ${index}`);
    if (!snapshot) return;
    addScene(snapshot);
    notify(`Saved ${snapshot.name}`);
  };

  const restoreScene = (scene: SavedScene) => {
    viewportRef.current?.restoreScene(scene);
    setStatus(`Restored ${scene.name}`);
  };

  return (
    <div className="studio-app">
      <TopBar
        onNotice={(message) => notify(message, 'info')}
        onAdChoices={openAdPreferences}
        onOpen={() => fileInputRef.current?.click()}
        onSave={() => { downloadModel(document); notify('Editable .ccmodel downloaded'); }}
        onExport={exportModel}
        onCapture={capture}
        onFit={(selectionOnly) => viewportRef.current?.fitView(selectionOnly)}
        onView={(view) => viewportRef.current?.setStandardView(view)}
        onProjection={() => viewportRef.current?.toggleProjection()}
        onHelp={() => setHelpOpen(true)}
        onNewBlank={() => { newBlank(); viewportRef.current?.setStandardView('iso'); notify('Created a blank model', 'info'); }}
        onNewDemo={() => { newDemo(); window.setTimeout(() => viewportRef.current?.fitView(false), 0); notify('Loaded demonstration model', 'info'); }}
      />
      <main className="workspace">
        <ToolPalette />
        <Viewport ref={viewportRef} />
        <Inspector onSaveScene={saveScene} onRestoreScene={restoreScene} />
        <AdRail />
      </main>
      <StatusBar />

      <input
        ref={fileInputRef}
        type="file"
        accept=".ccmodel,application/json,application/vnd.constructcanvas+json"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void openFile(file);
          event.currentTarget.value = '';
        }}
      />

      <AdPreferences />
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      {toast ? <Toast toast={toast} onClose={() => setToast(null)} /> : null}
    </div>
  );
}

function Toast({ toast, onClose }: { toast: ToastState; onClose: () => void }): JSX.Element {
  const Icon = toast.kind === 'error' ? AlertTriangle : toast.kind === 'success' ? CheckCircle2 : Info;
  return (
    <div className={`toast toast-${toast.kind}`} role="status">
      <Icon size={18} />
      <span>{toast.message}</span>
      <button type="button" onClick={onClose} aria-label="Dismiss notification"><X size={15} /></button>
    </div>
  );
}
